import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

/**
 * FlySummaryCardDropdown Plugin
 * Manages the collapse/expand state of a FlySummaryBarB2C component
 * based on a toggle button in ComSummaryCardDropdownB2C
 *
 * When collapsed: hasVisibleContent=false, hasDesglosePrecio=false
 *   - Hide FlySummaryBar slots container
 *   - Hide FlySummaryBar desglose button
 *   - Keep breakdown visible so CoreButton remains visible
 * When expanded: hasVisibleContent=true, hasDesglosePrecio=false
 *   - Show FlySummaryBar slots container
 *   - Keep FlySummaryBar desglose button hidden
 */
export class FlySummaryCardDropdown implements Plugin {
  private readonly toggleButton: HTMLElement | null;
  private readonly breakdown: HTMLElement | null;
  private readonly summaryBar: HTMLElement | null;
  private isExpanded: boolean = false;

  constructor(private readonly element: HTMLElement) {
    this.toggleButton = element.querySelector("[data-accordion-toggle]");
    this.breakdown = element.querySelector("[data-accordion-content]");
    this.summaryBar =
      element.querySelector("[data-summary-bar]") ??
      element.querySelector(".fly-summaryBarB2C");
  }

  init(): void {
    if (!this.toggleButton || !this.breakdown || !this.summaryBar) {
      console.warn(
        "FlySummaryCardDropdown: Required elements not found",
        this.element,
      );
      return;
    }

    // Initialize in collapsed state
    this.setCollapsed();
    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.toggleButton) return;

    this.toggleButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggle();
    });

    // Keyboard support
    this.toggleButton.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggle();
      }
    });

    // Ensure button is focusable
    if (!this.toggleButton.hasAttribute("tabindex")) {
      this.toggleButton.setAttribute("tabindex", "0");
    }
  }

  private toggle(): void {
    if (this.isExpanded) {
      this.setCollapsed();
    } else {
      this.setExpanded();
    }
  }

  private setCollapsed(): void {
    this.isExpanded = false;

    // Keep breakdown visible (only inner FlySummaryBar state changes)
    if (this.breakdown) {
      this.breakdown.style.display = "flex";
      this.breakdown.setAttribute("aria-hidden", "false");
    }

    // Remove expanded class from container
    this.element.classList.remove("is-expanded");

    // Hide slots in FlySummaryBarB2C (hasVisibleContent = false)
    this.hideSlotsContainer();
    this.setSummaryBarCollapsedState();

    // Hide desglose button (hasDesglosePrecio = false)
    this.hideDesglose();

    // Update button text and icon
    this.updateButtonState();

    this.dispatchEvent("flySummaryCardDropdownClosed");
  }

  private setExpanded(): void {
    this.isExpanded = true;

    // Keep breakdown visible (only inner FlySummaryBar state changes)
    if (this.breakdown) {
      this.breakdown.style.display = "flex";
      this.breakdown.setAttribute("aria-hidden", "false");
    }

    // Add expanded class to container
    this.element.classList.add("is-expanded");

    // Show slots in FlySummaryBarB2C (hasVisibleContent = true)
    this.showSlotsContainer();
    this.setSummaryBarExpandedState();

    this.showDesglose();

    // Update button text and icon
    this.updateButtonState();

    this.dispatchEvent("flySummaryCardDropdownOpened");
  }

  private getSlotsContainer(): Element | null {
    if (!this.summaryBar) return null;
    return (
      this.summaryBar.querySelector("[data-summary-bar-slots]") ??
      this.summaryBar.querySelector(".fly-summaryBarB2C__slots-container")
    );
  }

  private hideSlotsContainer(): void {
    const slotsContainer = this.getSlotsContainer();
    if (slotsContainer) {
      slotsContainer.classList.add(
        "fly-summaryBarB2C__slots-container--hidden",
      );
    }
  }

  private showSlotsContainer(): void {
    const slotsContainer = this.getSlotsContainer();
    if (slotsContainer) {
      slotsContainer.classList.remove(
        "fly-summaryBarB2C__slots-container--hidden",
      );
    }
  }

  private hideDesglose(): void {
    if (!this.summaryBar) return;

    const desgloseWrapper = this.summaryBar.querySelector(
      ".core-buttonOpenDetails__wrapper",
    );
    if (desgloseWrapper) {
      desgloseWrapper.classList.add("core-buttonOpenDetails__wrapper--hidden");
    }
  }

  private showDesglose(): void {
    console.log("showDesglose");
    if (!this.summaryBar) return;

    const desgloseWrapper = this.summaryBar.querySelector(
      ".core-buttonOpenDetails__wrapper",
    );
    if (desgloseWrapper) {
      desgloseWrapper.classList.remove(
        "core-buttonOpenDetails__wrapper--hidden",
      );
    }
  }

  private setSummaryBarCollapsedState(): void {
    if (!this.summaryBar) return;

    this.summaryBar.classList.add("fly-summaryBarB2C--dropdown-collapsed");
    this.summaryBar.classList.remove("fly-summaryBarB2C--dropdown-expanded");
  }

  private setSummaryBarExpandedState(): void {
    if (!this.summaryBar) return;

    this.summaryBar.classList.add("fly-summaryBarB2C--dropdown-expanded");
    this.summaryBar.classList.remove("fly-summaryBarB2C--dropdown-collapsed");
  }

  private updateButtonState(): void {
    if (!this.toggleButton) return;

    const labelElement = this.toggleButton.querySelector(
      ".core-buttonOpenDetails__label",
    );
    const iconElement = this.toggleButton.querySelector(
      ".core-buttonOpenDetails__icon.dsc-co-icon-chevron-down, .core-buttonOpenDetails__icon.dsc-co-icon-chevron-up",
    );

    if (labelElement) {
      labelElement.textContent = this.isExpanded
        ? "Ocultar detalles"
        : "Mostrar detalles";
    }

    if (iconElement) {
      if (this.isExpanded) {
        iconElement.className = iconElement.className.replace(
          "dsc-co-icon-chevron-down",
          "dsc-co-icon-chevron-up",
        );
      } else {
        iconElement.className = iconElement.className.replace(
          "dsc-co-icon-chevron-up",
          "dsc-co-icon-chevron-down",
        );
      }
    }
  }

  private dispatchEvent(name: string): void {
    this.element.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          isExpanded: this.isExpanded,
        },
      }),
    );
  }
}

PluginRegistry.register("fly-summary-card-dropdown", FlySummaryCardDropdown);
